import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, X, BookOpen, Calendar, User } from 'lucide-react';
import '../../styles/StoriesPage.css';
import { API_BASE_URL } from '../../config/api';

const StoriesPage = ({ currentUser }) => {
  const [stories, setStories] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  // Form State
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [imageUrl, setImageUrl] = useState('');

  // Fetch Stories
  useEffect(() => {
    fetchStories();
  }, []);

  const fetchStories = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/stories`);
      const data = await response.json();
      if (response.ok) setStories(data);
    } catch (err) {
      console.error("Error loading stories:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    let token = localStorage.getItem('token');
    if (!token) {
      alert("Session expired or missing. Please sign in to publish a story.");
      return;
    }

    // Clean up the token string if it accidentally contains a duplicate Bearer prefix
    if (token.startsWith('Bearer ')) {
      token = token.replace('Bearer ', '');
    }

    try {
      console.log("Sending story payload to:", `${API_BASE_URL}/stories`);
      
      const response = await fetch(`${API_BASE_URL}/stories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ title, content, imageUrl })
      });

      const data = await response.json();

      if (response.ok) {
        console.log("Story published successfully:", data);
        
        // Prepend the new story with populated author data back into the feed state
        setStories((prevStories) => [data, ...prevStories]);
        
        // Reset fields and smoothly collapse modal frame
        setTitle('');
        setContent('');
        setImageUrl('');
        setIsModalOpen(false);
      } else {
        console.error("Server validation error:", data);
        alert(`Failed to publish: ${data.message || 'Check form fields and try again.'}`);
      }
    } catch (err) {
      console.error("Network or submission fault:", err);
      alert("Network connectivity error. Please verify your backend server is running.");
    }
  };

  return (
    <div className="stories-container">
      
      {/* Header section */}
      <div className="stories-header">
        <div>
          <h1>Tribe Stories</h1>
          <p>Read and share custom car builds, deep-dives, and personal write-ups.</p>
        </div>
        
        {localStorage.getItem('token') && (
          <button onClick={() => setIsModalOpen(true)} className="create-story-btn">
            <Plus size={18} /> Share Your Story
          </button>
        )}
      </div>

      {/* Main Feed Grid */}
      {loading ? (
        <p className="stories-loading">Loading the latest stories...</p>
      ) : (
        <div className="stories-grid">
          {stories.map((story) => (
            <motion.div 
              key={story._id}
              layout
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="story-card"
            >
              {story.imageUrl && (
                <img 
                  src={story.imageUrl} 
                  alt={story.title} 
                  className="story-card-img"
                />
              )}
              <div className="story-card-body">
                <div>
                  <h3>{story.title}</h3>
                  <p>
                    {story.content.length > 150 ? `${story.content.substring(0, 150)}...` : story.content}
                  </p>
                </div>
                
                <div className="story-meta">
                  <span>
                    <User size={14} /> {story.author?.username || 'Tribe Member'}
                  </span>
                  <span>
                    <Calendar size={14} /> {new Date(story.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* COMPACT MODAL POPUP */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="modal-overlay">
            {/* Backdrop */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsModalOpen(false)}
              className="modal-backdrop"
            />
            
            {/* Modal Body */}
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="modal-content"
            >
              <div className="modal-header">
                <h2>
                  <BookOpen style={{ color: 'var(--primary-blue)' }} /> Create a New Story
                </h2>
                <button onClick={() => setIsModalOpen(false)} className="modal-close-btn">
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Story Title</label>
                  <input 
                    type="text" 
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g., Restoring my 1992 E30 over the weekend"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Image URL</label>
                  <input 
                    type="text" 
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    placeholder="Paste an image reference link here (Optional)"
                  />
                </div>

                <div className="form-group">
                  <label>Content</label>
                  <textarea 
                    rows="6"
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder="Write your story details or article here..."
                    required
                  />
                </div>

                {/* Button explicitly kept as type="submit" to properly fire form onSubmit */}
                <button type="submit" className="form-submit-btn">
                  Publish Story
                </button>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default StoriesPage;